import { formatCount } from '@/lib/utils';
import { EOLIngestionStatus } from '@/models/docs';
import { CheckedState } from '@radix-ui/react-checkbox';

import {
    BulkDeleteResultCopy,
    BulkFileDeletionResult,
    DocumentIngestionStatus,
    DocumentSummary,
    FileStatus,
    RepositoryNode,
    TreeRow,
    isFileNode,
    isFolderNode,
} from './types';

const ERROR_STATUSES: EOLIngestionStatus[] = [
    EOLIngestionStatus.FAILED,
    EOLIngestionStatus.REQUIRES_REINGESTION,
];

export function isErrorStatus(status: DocumentIngestionStatus): boolean {
    return status !== null && ERROR_STATUSES.includes(status);
}

export function isPending(status: DocumentIngestionStatus): boolean {
    return status === EOLIngestionStatus.PENDING;
}

export function isQueued(status: DocumentIngestionStatus): boolean {
    return status === EOLIngestionStatus.QUEUED;
}

export function isInProgress(status: DocumentIngestionStatus): boolean {
    return status === EOLIngestionStatus.IN_PROGRESS;
}

export function isProcessingStatus(status: DocumentIngestionStatus): boolean {
    return isPending(status) || isQueued(status) || isInProgress(status);
}

/**
 * Render a long file path as `first/…/…/last`, collapsing the middle segments
 * to `…`. Paths with 3 or fewer segments are returned unchanged. Leading and
 * trailing slashes are preserved so folder paths (e.g. "/A/B/C/") keep their
 * shape. Always set the full path as the cell `title`/tooltip alongside this.
 */
export function truncateFilePath(path: string): string {
    if (!path) return path;

    const hasLeadingSlash = path.startsWith('/');
    const hasTrailingSlash = path.length > 1 && path.endsWith('/');

    const segments = path.split('/').filter(Boolean);
    if (segments.length <= 3) return path;

    const collapsed = [segments[0], '…', '…', segments[segments.length - 1]].join(
        '/',
    );

    return `${hasLeadingSlash ? '/' : ''}${collapsed}${hasTrailingSlash ? '/' : ''}`;
}

/** Document types that support the human-correction (edit) flow. */
export const CORRECTABLE_TYPES = [
    'ad',
    'eo',
    'arc',
    'sb',
    'dr',
    'rdas',
    'afm',
    'amp',
    'sv',
    'dir',
    'tc',
];

/** Derive a folder's checkbox state from its descendant files' selection. */
export function getFolderSelectionState(
    fileIds: number[],
    selectedFileIds: Set<number>,
): CheckedState {
    if (fileIds.length === 0) return false;

    const selectedCount = fileIds.filter((id) => selectedFileIds.has(id)).length;
    if (selectedCount === fileIds.length) return true;
    if (selectedCount === 0) return false;
    return 'indeterminate';
}

// A selectable row in the flattened tree. A file row owns its own id; a folder
// row owns every descendant file id, so ranges over collapsed folders still
// select the hidden contents.
export interface FlatRow {
    rowKey: string;
    fileIds: number[];
}

export function fileRowKey(id: number): string {
    return `file-${id}`;
}

export function folderRowKey(key: string): string {
    return `folder-${key}`;
}

export type ParsedRowKey =
    | { kind: 'folder'; key: string }
    | { kind: 'file'; id: number }
    | null;

export function parseRowKey(rowKey: string): ParsedRowKey {
    if (rowKey.startsWith('folder-')) {
        return { kind: 'folder', key: rowKey.slice('folder-'.length) };
    }
    if (rowKey.startsWith('file-')) {
        return { kind: 'file', id: Number(rowKey.slice('file-'.length)) };
    }
    return null;
}

// DFS pre-order (folder before its children). This matches visible order no
// matter what's expanded, which is the ordering a contiguous range needs.
export function flattenSelectableRows(nodes: RepositoryNode[]): FlatRow[] {
    const rows: FlatRow[] = [];
    const walk = (level: RepositoryNode[]) => {
        level.forEach((node) => {
            if (isFolderNode(node)) {
                rows.push({
                    rowKey: folderRowKey(node.key),
                    fileIds: collectAllFileIds(node.children),
                });
                walk(node.children);
            } else {
                rows.push({ rowKey: fileRowKey(node.id), fileIds: [node.id] });
            }
        });
    };
    walk(nodes);
    return rows;
}

// Union of the file ids of every row between the two keys (inclusive,
// order-independent); [] if either key is missing.
export function fileIdsInRange(
    rows: FlatRow[],
    anchorKey: string,
    targetKey: string,
): number[] {
    const anchorIndex = rows.findIndex((r) => r.rowKey === anchorKey);
    const targetIndex = rows.findIndex((r) => r.rowKey === targetKey);
    if (anchorIndex === -1 || targetIndex === -1) return [];
    const [start, end] =
        anchorIndex <= targetIndex
            ? [anchorIndex, targetIndex]
            : [targetIndex, anchorIndex];
    const ids = new Set<number>();
    for (let i = start; i <= end; i++) {
        rows[i].fileIds.forEach((id) => ids.add(id));
    }
    return [...ids];
}

// Only files carry an id; a folder's checkbox cascades to its descendant files.
export function collectAllFileIds(nodes: RepositoryNode[]): number[] {
    const fileIds: number[] = [];
    const walk = (level: RepositoryNode[]) => {
        level.forEach((node) => {
            if (isFolderNode(node)) walk(node.children);
            else fileIds.push(node.id);
        });
    };
    walk(nodes);
    return fileIds;
}

// The tree selects file ids but the move endpoint takes document ids. A file with no
// documents (still processing, or not a document) contributes nothing.
export function documentIdsForFiles(
    tree: RepositoryNode[],
    selectedFileIds: Set<number>,
): number[] {
    const documentIds: number[] = [];
    const walk = (level: RepositoryNode[]) => {
        level.forEach((node) => {
            if (isFolderNode(node)) {
                walk(node.children);
            } else if (selectedFileIds.has(node.id)) {
                node.documents.forEach((doc) => documentIds.push(doc.id));
            }
        });
    };
    walk(tree);
    return documentIds;
}

export interface SelectionSummary {
    folderCount: number;
    fileCount: number;
    requestedCount: number;
}

export function selectionSummary(
    nodes: RepositoryNode[],
    selectedFileIds: Set<number>,
): SelectionSummary {
    let folderCount = 0;
    let fileCount = 0;
    for (const node of nodes) {
        if (isFolderNode(node)) {
            const hasSelectedDescendant = collectAllFileIds(node.children).some(
                (id) => selectedFileIds.has(id),
            );
            if (hasSelectedDescendant) folderCount++;
        } else if (selectedFileIds.has(node.id)) {
            fileCount++;
        }
    }
    return { folderCount, fileCount, requestedCount: selectedFileIds.size };
}

// Both args must be top-level node counts, never a descendant-file count
// (different units, was a bug).
export function canOfferSelectAllAcrossRepo(
    isPageFullySelected: boolean,
    pageTopLevelCount: number,
    repoTopLevelTotal: number,
): boolean {
    return isPageFullySelected && repoTopLevelTotal > pageTopLevelCount;
}

export function acrossRepoSubject(count: number): string {
    return `${count} ${count === 1 ? 'folder or file' : 'folders and files'}`;
}

// Remove the given files from the tree, pruning any folder they empty. Pure,
// and returns the same reference when nothing changed.
export function removeFilesFromTree(
    nodes: RepositoryNode[],
    fileIds: Set<number>,
): RepositoryNode[] {
    let changed = false;
    const result: RepositoryNode[] = [];
    for (const node of nodes) {
        if (isFileNode(node)) {
            if (fileIds.has(node.id)) changed = true;
            else result.push(node);
            continue;
        }
        const children = removeFilesFromTree(node.children, fileIds);
        if (children.length === 0) {
            changed = true;
            continue;
        }
        if (children === node.children) result.push(node);
        else {
            changed = true;
            result.push({ ...node, children });
        }
    }
    return changed ? result : nodes;
}

// Mirrors the backend's _folder_status aggregation: Error > Processing > Complete
function deriveFolderStatus(children: RepositoryNode[]): FileStatus {
    const statuses = new Set(children.map((child) => child.status));
    if (statuses.has('error')) return 'error';
    if (statuses.has('processing')) return 'processing';
    return 'ready';
}

export function treeHasProcessingNode(nodes: RepositoryNode[]): boolean {
    return nodes.some((node) => node.status === 'processing');
}

// Set the status of the given files in the tree, re-deriving the status of any
// folder whose descendants changed. Pure, and returns the same reference when
// nothing changed.
export function setFileStatusInTree(
    nodes: RepositoryNode[],
    fileIds: Set<number>,
    status: FileStatus,
): RepositoryNode[] {
    let changed = false;
    const result = nodes.map((node) => {
        if (isFileNode(node)) {
            if (!fileIds.has(node.id) || node.status === status) return node;
            changed = true;
            return { ...node, status };
        }
        const children = setFileStatusInTree(node.children, fileIds, status);
        if (children === node.children) return node;
        changed = true;
        return { ...node, children, status: deriveFolderStatus(children) };
    });
    return changed ? result : nodes;
}

// Remove a document from its owning file and decrement its documentCount. Pure,
// and returns the same reference when nothing changed.
export function removeDocumentFromTree(
    nodes: RepositoryNode[],
    docId: number,
): RepositoryNode[] {
    let changed = false;
    const result = nodes.map((node) => {
        if (isFolderNode(node)) {
            const children = removeDocumentFromTree(node.children, docId);
            if (children === node.children) return node;
            changed = true;
            return { ...node, children };
        }
        const documents = node.documents.filter((doc) => doc.id !== docId);
        if (documents.length === node.documents.length) return node;
        changed = true;
        return {
            ...node,
            documents,
            documentCount: Math.max(0, node.documentCount - 1),
        };
    });
    return changed ? result : nodes;
}

export interface DocumentStatusCounts {
    completed: number;
    processing: number;
    failed: number;
}

export function getDocumentStatusCounts(
    documents: DocumentSummary[],
): DocumentStatusCounts {
    return documents.reduce(
        (counts, doc) => {
            const hasPartialExtractionError =
                doc.ingestionWarnings?.some(
                    (w) =>
                        w.warningType === 'partial_line_item_extraction',
                ) ?? false;
            if (
                isErrorStatus(doc.ingestionStatus) ||
                hasPartialExtractionError
            ) {
                counts.failed++;
            } else if (isProcessingStatus(doc.ingestionStatus)) {
                counts.processing++;
            } else {
                counts.completed++;
            }
            return counts;
        },
        { completed: 0, processing: 0, failed: 0 },
    );
}

const DEFAULT_RESULT_BUTTON_LABEL = 'Continue';

export function bulkDeleteSubject(folderCount: number, fileCount: number): string {
    return [
        folderCount > 0 && formatCount(folderCount, 'folder', 'folders'),
        fileCount > 0 && formatCount(fileCount, 'file', 'files'),
    ]
        .filter(Boolean)
        .join(' and ');
}

type BulkDeleteReasonCategory = 'timeout' | 'permission' | 'generic';

interface ReasonCopy {
    category: BulkDeleteReasonCategory;
    title: string;
    note?: string;
}

const TIMEOUT_REASON: ReasonCopy = {
    category: 'timeout',
    title: 'Server timeout',
    note: 'Try again to delete the remaining files.',
};

const PERMISSION_REASON: ReasonCopy = {
    category: 'permission',
    title: 'Permission required',
    note: 'Contact your workspace administrator if you need this permission.',
};

const GENERIC_REASON: ReasonCopy = {
    category: 'generic',
    title: 'Some items were not deleted',
    note: 'Try again to delete the remaining files.',
};

// Most-severe first, for picking a header when failed[] mixes categories.
const REASON_SEVERITY: Record<BulkDeleteReasonCategory, number> = {
    permission: 2,
    timeout: 1,
    generic: 0,
};

// Maps the backend's raw reason (an exception class name) to known copy, with a
// generic fallback so the raw value is never shown.
function reasonToCopy(reason: string): ReasonCopy {
    const normalized = reason.toLowerCase();
    if (normalized.includes('timeout')) return TIMEOUT_REASON;
    if (
        normalized.includes('permission') ||
        normalized.includes('forbidden') ||
        normalized.includes('unauthorized')
    ) {
        return PERMISSION_REASON;
    }
    return GENERIC_REASON;
}

function dominantReason(failed: { reason: string }[]): ReasonCopy {
    return failed
        .map((item) => reasonToCopy(item.reason))
        .reduce((dominant, current) =>
            REASON_SEVERITY[current.category] > REASON_SEVERITY[dominant.category]
                ? current
                : dominant,
        );
}

// "was"/"were" agreeing with the count.
function wasWere(count: number): string {
    return count === 1 ? 'was' : 'were';
}

function deletedSummary(deletedCount: number, requestedCount: number): string {
    return `${deletedCount} of ${formatCount(requestedCount, 'file', 'files')} ${wasWere(deletedCount)} permanently deleted.`;
}

export function bulkDeleteResultCopy({
    folderCount,
    fileCount,
    requestedCount,
    subject,
    deletedCount,
    alreadyDeletedCount,
    failed,
    buttonLabel = DEFAULT_RESULT_BUTTON_LABEL,
}: BulkFileDeletionResult & {
    folderCount: number;
    fileCount: number;
    requestedCount: number;
    subject?: string;
    buttonLabel?: string;
}): BulkDeleteResultCopy {
    if (failed.length === 0 && alreadyDeletedCount === 0) {
        return {
            title: 'Deletion complete',
            body: `The ${subject ?? bulkDeleteSubject(folderCount, fileCount)}, including all their documents, were permanently deleted.`,
            buttonLabel,
        };
    }

    // Repository-wide requestedCount is a node count, not comparable to the
    // response's file counts, so derive the "of N" total from the response.
    const effectiveTotal =
        subject !== undefined
            ? deletedCount + alreadyDeletedCount + failed.length
            : requestedCount;

    // Already deleted by another user is success-with-note, not a failure.
    if (failed.length === 0) {
        return {
            title: 'Deletion complete',
            body: `${deletedSummary(deletedCount, effectiveTotal)} ${formatCount(alreadyDeletedCount, 'file', 'files')} had already been deleted by another user.`,
            buttonLabel,
        };
    }

    const categories = new Set(failed.map((item) => reasonToCopy(item.reason).category));

    if (categories.size > 1) {
        // TODO(copy): mixed reasons unspecified; use the dominant reason's header.
        const dominant = dominantReason(failed);
        return {
            title: dominant.title,
            body: `${deletedSummary(deletedCount, effectiveTotal)} ${formatCount(failed.length, 'file', 'files')} ${wasWere(failed.length)} not deleted.`,
            note: dominant.note,
            buttonLabel,
        };
    }

    const { category, title, note } = reasonToCopy(failed[0].reason);

    if (category === 'permission') {
        return {
            title,
            body: 'Your role was changed and you no longer have permission to delete files. No items were deleted.',
            note,
            buttonLabel,
        };
    }

    if (category === 'timeout') {
        return {
            title,
            body: `The server timed out before all items could be deleted. ${deletedSummary(deletedCount, effectiveTotal)} ${formatCount(failed.length, 'file', 'files')} ${wasWere(failed.length)} not deleted and ${failed.length === 1 ? 'remains' : 'remain'} in the document repository.`,
            note,
            buttonLabel,
        };
    }

    return {
        title,
        body: `${deletedSummary(deletedCount, effectiveTotal)} ${formatCount(failed.length, 'file', 'files')} ${wasWere(failed.length)} not deleted.`,
        note,
        buttonLabel,
    };
}

function documentRowKey(id: number): string {
    return `doc-${id}`;
}

export function flattenVisibleRows(
    nodes: RepositoryNode[],
    expandedFolders: Set<string>,
    expandedFiles: Set<number>,
): TreeRow[] {
    const rows: TreeRow[] = [];
    const walk = (
        children: RepositoryNode[],
        depth: number,
        parentIndex: number,
    ) => {
        for (const node of children) {
            const index = rows.length;
            if (isFolderNode(node)) {
                rows.push({
                    kind: 'folder',
                    folder: node,
                    depth,
                    parentIndex,
                    rowKey: folderRowKey(node.key),
                });
                if (expandedFolders.has(node.key))
                    walk(node.children, depth + 1, index);
            } else {
                rows.push({
                    kind: 'file',
                    file: node,
                    depth,
                    parentIndex,
                    rowKey: fileRowKey(node.id),
                });
                if (expandedFiles.has(node.id)) {
                    for (const doc of node.documents) {
                        rows.push({
                            kind: 'document',
                            file: node,
                            doc,
                            depth,
                            parentIndex: index,
                            rowKey: documentRowKey(doc.id),
                        });
                    }
                }
            }
        }
    };
    walk(nodes, 0, -1);
    return rows;
}

export function holdingFolderIndex(rows: TreeRow[], at: number): number {
    if (!rows[at] || rows[at].kind === 'folder') return -1;
    let parentIndex = rows[at].parentIndex;
    while (parentIndex >= 0 && rows[parentIndex].kind !== 'folder') {
        parentIndex = rows[parentIndex].parentIndex;
    }
    return parentIndex;
}

export function navigableRowKeys(rows: TreeRow[]): string[] {
    return rows
        .filter((row) => row.kind !== 'document')
        .map((row) => row.rowKey);
}

export function scrollParentOf(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
        if (['auto', 'scroll'].includes(getComputedStyle(parent).overflowY))
            return parent;
        parent = parent.parentElement;
    }
    return null;
}
